import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Wrench } from 'lucide-react';

import {
  STUDIO_CREATE_RESOURCE_OPTIONS,
  type StudioCreateResourceType,
} from '../constants/studioCreateConstants';

export interface CreateMaterialChooserProps {
  subjectLabel: string;
  onGenerate: (resourceType?: StudioCreateResourceType) => void;
  /** Enlace al Taller interactivo filtrado por asignatura. */
  tallerHref: string;
}

/**
 * Crear material (guía / rúbrica / presentación) — vive en asignatura o curso.
 * Las mecánicas de sala van al Taller interactivo.
 */
export function CreateMaterialChooser({
  subjectLabel,
  onGenerate,
  tallerHref,
}: CreateMaterialChooserProps) {
  return (
    <section className="course-workspace__create">
      <header className="course-workspace__create-head">
        <div className="course-workspace__create-copy min-w-0 flex-1">
          <h2 className="course-workspace__create-title">Crear material</h2>
          <p className="course-workspace__create-hint">
            Guías, rúbricas y presentaciones con el contexto de{' '}
            <strong>{subjectLabel}</strong>. Las dinámicas de sala están en el Taller
            interactivo.
          </p>
        </div>
        <button type="button" className="course-workspace__cta" onClick={() => onGenerate('guide')}>
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          Generar con IA
        </button>
      </header>

      <div className="use-case-grid mt-5">
        {STUDIO_CREATE_RESOURCE_OPTIONS.map((option) => {
          const Icon = option.Icon;
          return (
            <button
              key={option.id}
              type="button"
              className="use-case-card text-left"
              onClick={() => onGenerate(option.id)}
            >
              <span
                className="use-case-card__icon"
                style={{
                  background:
                    option.accent === 'mint'
                      ? 'color-mix(in srgb, var(--micelio-mint, #7eb8a2) 22%, #fff)'
                      : 'color-mix(in srgb, var(--micelio-terracota, #e07a5f) 18%, #fff)',
                }}
                aria-hidden
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <span className="use-case-card__body">
                <span className="use-case-card__badge">Material</span>
                <span className="use-case-card__title">{option.title}</span>
                <span className="use-case-card__desc">{option.subtitle}</span>
              </span>
              <span className="use-case-card__arrow" aria-hidden>
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-dashed px-4 py-4 sm:px-5"
        style={{ borderColor: 'var(--sala-border-strong)' }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex items-start gap-2.5">
            <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-[var(--sala-ink-dim)]" aria-hidden />
            <div>
              <p className="text-sm font-semibold text-[var(--sala-ink)]">Taller interactivo</p>
              <p className="mt-0.5 text-xs text-[var(--sala-ink-muted)]">
                Recetas y motores en vivo (arena, poll, coop, partitura…).
              </p>
            </div>
          </div>
          <Link to={tallerHref} className="course-workspace__cta course-workspace__cta--compact">
            Abrir taller
            <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}
